/**
 * STAIR STUDIO stage 5 (2026-10-09) — GUARDS AND RAILS (docs/bim-plan/30-stair-studio.md; Matt:
 * guards are REAL railing elements the Studio places, hosted on the stair).
 *
 * A railing with a `stairSide` ('left' / 'right', as the walker going up sees it) hosted on a
 * stair takes its line from the stair itself — this file — instead of from its own drawn path:
 * the stair's side, set in by the mounting, with two heights at every point:
 *   zNose  — the nosing line on a flight (the line a handrail is measured from), the floor on a
 *            landing;
 *   zFloor — what a baluster stands on there: the tread top, the landing, the floor.
 * Straight / L / U stairs walk the layout's two edge chains (landings included, corners kept);
 * spiral and helical stairs walk their arcs. The railing's 3D (bim-geometry
 * railing/stairGuard.ts), its plan line, the Studio and the code checks all read this.
 */
import type { Pt2 } from './types'
import { stairFactsOf, type StairFacts } from './stairCode'
import { stairLegEnd } from './stairLayout'

export interface StairGuardPoint {
  p: Pt2; zNose: number; zFloor: number; level: boolean
  /** the flight the line runs along FROM this point to the next (layout leg index); −1 = a
   *  landing or the floor; −2 = a curved stair. Every post and baluster on that piece takes its
   *  heights from THIS flight — never from whichever flight happens to be nearest (a U's two
   *  flights can touch along the well). */
  leg: number
}

/** the catalog the Studio offers (stairGuard.ts in geometry builds each) */
export const GUARD_KINDS = [
  { value: 'guard', label: 'Guard', note: 'A guard on the open side, its handrail on top' },
  { value: 'wallRail', label: 'Wall rail', note: 'A handrail on brackets, 1-1/2" off the wall' },
  { value: 'recessed', label: 'Recessed rail', note: 'A handrail set into a pocket in the wall, lit from inside' },
] as const
export const GUARD_INFILLS = [
  { value: 'balusters', label: 'Wood balusters', note: 'Square balusters, two to a tread' },
  { value: 'iron', label: 'Forged iron', note: '1/2" square iron balusters' },
  { value: 'pickets', label: 'Flat-bar pickets', note: 'Steel flat bar at 4" clear' },
  { value: 'cableH', label: 'Horizontal cable', note: 'Stainless cable at 3" (check climbability)' },
  { value: 'cableV', label: 'Vertical rods', note: 'Steel rods at 4" clear' },
  { value: 'glassShoe', label: 'Glass in a shoe', note: 'Laminated glass in a continuous base shoe' },
  { value: 'glassStandoff', label: 'Glass on standoffs', note: 'Glass on stainless standoffs off the stringer' },
  { value: 'glassPosts', label: 'Glass between posts', note: 'Glass panels clamped between posts' },
  { value: 'mesh', label: 'Perforated panels', note: 'Framed perforated steel panels' },
  { value: 'kneeWall', label: 'Solid knee wall', note: 'A plastered half wall with a cap' },
  { value: 'none', label: 'Rail only', note: 'Posts and a rail, no infill (where no guard is required)' },
] as const
export const GUARD_RAILS = [
  { value: 'round', label: 'Round 1-1/2"' },
  { value: 'oval', label: 'Oval' },
  { value: 'rectWood', label: 'Flat wood' },
  { value: 'squareBar', label: 'Square bar' },
  { value: 'cap', label: 'Flat cap' },
  { value: 'none', label: 'None' },
] as const
export const GUARD_NEWELS = [
  { value: 'box', label: 'Box newel' },
  { value: 'turned', label: 'Turned newel' },
  { value: 'post', label: 'Steel post' },
  { value: 'none', label: 'None' },
] as const

export interface StairGuardSpec {
  kind: 'guard' | 'wallRail' | 'recessed'
  infill: string
  rail: string
  newel: string
  mount: 'top' | 'fascia'
  railHeightIn: number
  guardHeightIn: number
}

const IN = 1 / 12

/**
 * A guard's look: the instance's own value (a Studio guard's per-stair choice) where it has one,
 * else its TYPE's (railStyle 'guard'), else the catalog default. Pass a resolved railing's
 * `params` and `typeParams`.
 */
export function stairGuardSpecOf(params: Record<string, unknown>, typeParams: Record<string, unknown> = {}): StairGuardSpec {
  const pick = (k: string, ok: readonly { value: string }[], d: string) => {
    for (const v of [params[k], typeParams[k]]) if (ok.some((o) => o.value === v)) return String(v)
    return d
  }
  const num = (k: string, d: number) => {
    for (const v of [params[k], typeParams[k]]) { const n = Number(v); if (v !== null && v !== '' && v !== undefined && Number.isFinite(n) && n > 0) return n }
    return d
  }
  return {
    kind: pick('guardKind', GUARD_KINDS, 'guard') as StairGuardSpec['kind'],
    infill: pick('guardInfill', GUARD_INFILLS, 'balusters'),
    rail: pick('guardRail', GUARD_RAILS, 'round'),
    newel: pick('guardNewel', GUARD_NEWELS, 'box'),
    mount: pick('guardMount', [{ value: 'top' }, { value: 'fascia' }], 'top') as StairGuardSpec['mount'],
    railHeightIn: num('railHeightIn', 36),
    guardHeightIn: num('guardHeightIn', 36),
  }
}

/** a guard's material for one part: its own pick, else its type's ('' = the look's standard) */
export function stairGuardMaterialOf(params: Record<string, unknown>, typeParams: Record<string, unknown>, key: 'railMaterial' | 'infillMaterial' | 'newelMaterial'): string {
  const v = params[key] || typeParams[key]
  return typeof v === 'string' ? v : ''
}

/** how far in from the stair's edge the guard's line runs (negative = outside, fascia) */
export function stairGuardInsetFt(spec: StairGuardSpec): number {
  if (spec.kind === 'wallRail') return 2.25 * IN        // 1-1/2" clear + half the 1-1/2" rail
  if (spec.kind === 'recessed') return -0.75 * IN       // in its pocket, behind the wall face
  if (spec.infill === 'kneeWall') return 2.25 * IN     // a half wall always stands on the stair
  if (spec.mount === 'fascia') return -1.5 * IN
  return 1.25 * IN
}

/**
 * Offset an open polyline sideways (`d` > 0 = to its left) with mitered corners — except where
 * it doubles back on itself (a U whose flights touch: the inner edge runs up one flight and
 * straight back down the next), where the two offset pieces are joined by a short connector
 * instead of a runaway miter. `legs[i]` is segment i's flight; the connector gets −1 (landing).
 */
function offsetLine(pts: Pt2[], legs: number[], d: number): { p: Pt2; leg: number }[] {
  const n = pts.length
  const nrm = (a: Pt2, b: Pt2): Pt2 => { const L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1; return [-(b[1] - a[1]) / L, (b[0] - a[0]) / L] }
  const out: { p: Pt2; leg: number }[] = []
  for (let i = 0; i < n; i++) {
    const nA = i > 0 ? nrm(pts[i - 1], pts[i]) : null, nB = i < n - 1 ? nrm(pts[i], pts[i + 1]) : null
    const legOut = i < n - 1 ? legs[i] : -1
    if (nA && nB) {
      if (nA[0] * nB[0] + nA[1] * nB[1] < -0.5) {
        // a reversal: end the incoming piece, a connector across, start the outgoing piece
        out.push({ p: [pts[i][0] + nA[0] * d, pts[i][1] + nA[1] * d], leg: -1 })
        out.push({ p: [pts[i][0] + nB[0] * d, pts[i][1] + nB[1] * d], leg: legOut })
        continue
      }
      const m: Pt2 = [nA[0] + nB[0], nA[1] + nB[1]]
      const mL = Math.hypot(m[0], m[1])
      const k = mL > 1e-6 ? d / Math.max(0.25, (m[0] * nA[0] + m[1] * nA[1]) / mL) / mL : d
      out.push({ p: [pts[i][0] + m[0] * k, pts[i][1] + m[1] * k], leg: legOut })
    } else {
      const q = (nA ?? nB)!
      out.push({ p: [pts[i][0] + q[0] * d, pts[i][1] + q[1] * d], leg: legOut })
    }
  }
  return out
}

/**
 * The heights at any plan point of a stair (the guard's balusters and posts ask this between
 * the line's corners): the nosing line and the tread / landing / floor under it.
 */
export function stairGuardHeights(stairEl: Parameters<typeof stairFactsOf>[0]): ((p: Pt2, leg?: number) => { zNose: number; zFloor: number; level: boolean }) | null {
  const f = stairFactsOf(stairEl)
  if (!f?.layout) return null
  const c = f.layout.curve
  if (c) {
    const r = f.calc.riserFt, N = f.calc.treads
    return (p) => {
      let a = Math.atan2(p[1] - c.center[1], p[0] - c.center[0]) - c.theta0
      a = c.hand * a
      while (a < -1e-6) a += 2 * Math.PI
      // a spiral turns past a full circle: take the turn nearest the stair's own range
      let phi = a / c.dTheta
      const turn = (2 * Math.PI) / c.dTheta
      while (phi > N + 0.5 && phi - turn > -0.5) phi -= turn
      phi = Math.max(0, Math.min(N, phi))
      return { zNose: f.zBase + Math.min(f.riseFt, r + phi * r), zFloor: f.zBase + Math.min(f.riseFt, (Math.floor(phi + 1e-6) + 1) * r), level: false }
    }
  }
  return (p, leg) => heightsAt(f, p, leg)
}

/**
 * The heights at a point taken from a NAMED flight, its nosing line EXTENDED past both ends: up
 * to the landing or floor it arrives at (a flight's last tread is flush with its landing, so the
 * nosing line flattens one tread early) and back down to the landing or floor it leaves from (one
 * tread before its first riser) — the line a handrail follows, sloped at the stair's own pitch
 * and level where it meets a landing, never a straight line drawn between corners.
 */
function heightsOnLeg(f: StairFacts, li: number, p: Pt2): { zNose: number; zFloor: number; level: boolean } {
  const L = f.layout!
  const r = f.calc.riserFt, td = f.treadFt
  const g = L.legs[li]
  const nT = g.kEnd - g.kStart
  const s = Math.min(g.s0 + nT * td, (p[0] - g.a[0]) * g.u[0] + (p[1] - g.a[1]) * g.u[1])
  const end = stairLegEnd(L, li, f.riseFt)
  const top = !end.top                        // (true = it arrives at a landing)
  const zTopLeg = f.zBase + end.zEnd
  const Z0 = f.zBase + g.z0
  const zNose = Math.max(Z0, Math.min(zTopLeg, Z0 + r + ((s - g.s0) * r) / td))
  const j = Math.floor((s - g.s0) / td + 1e-6)
  const zFloor = s < g.s0 - 1e-6 ? Z0 : Math.min(zTopLeg, Z0 + (j + 1) * r)
  const level = zNose <= Z0 + 1e-6 || (top && zNose >= zTopLeg - 1e-6)
  return { zNose, zFloor, level }
}

/** where along a piece (0..1) a flight's extended nosing line kinks: where it leaves the landing
 *  or floor below and where it reaches the one above */
function legKinks(f: StairFacts, li: number, a: Pt2, b: Pt2): number[] {
  const L = f.layout!
  const r = f.calc.riserFt, td = f.treadFt
  const g = L.legs[li]
  const zTopRel = stairLegEnd(L, li, f.riseFt).zEnd - g.z0
  const sa = (a[0] - g.a[0]) * g.u[0] + (a[1] - g.a[1]) * g.u[1]
  const sb = (b[0] - g.a[0]) * g.u[0] + (b[1] - g.a[1]) * g.u[1]
  if (Math.abs(sb - sa) < 1e-6) return []
  const out: number[] = []
  for (const sk of [g.s0 - td, g.s0 + ((zTopRel - r) / r) * td]) {
    const t = (sk - sa) / (sb - sa)
    if (t > 1e-4 && t < 1 - 1e-4) out.push(t)
  }
  return out.sort((x, y) => x - y)
}

/** the winder turn a point is in or beside (index), or -1 */
function winderZoneAt(f: StairFacts, p: Pt2): number {
  const zs = f.layout?.winderZones ?? []
  for (let i = 0; i < zs.length; i++) if (distToPoly(p, zs[i].poly) < 0.3) return i
  return -1
}

/**
 * The heights over a winder turn: the nosing line climbs one riser per winder as the point
 * swings round the turn's fan point (continuous — a rail over winders climbs smoothly, steeper
 * on the inner side, as it really does); the floor is the winder under it.
 */
function winderHeights(f: StairFacts, zi: number, p: Pt2): { zNose: number; zFloor: number; level: boolean } {
  const z = f.layout!.winderZones![zi]
  const r = f.calc.riserFt
  const ang = (v: Pt2) => Math.atan2(v[1], v[0])
  const a0 = ang([z.walk[0][0] - z.q[0], z.walk[0][1] - z.q[1]])
  const norm = (a: number) => { let x = a - a0; while (x > Math.PI) x -= 2 * Math.PI; while (x < -Math.PI) x += 2 * Math.PI; return x }
  const rays = z.walk.map((w) => norm(ang([w[0] - z.q[0], w[1] - z.q[1]])))
  const sgn = Math.sign(rays[rays.length - 1]) || 1
  const a = norm(ang([p[0] - z.q[0], p[1] - z.q[1]])) * sgn
  let phi = 0
  if (a <= 0) phi = 0
  else if (a >= rays[rays.length - 1] * sgn) phi = z.m
  else for (let j = 0; j < z.m; j++) {
    const lo = rays[j] * sgn, hi = rays[j + 1] * sgn
    if (a >= lo && a <= hi) { phi = j + (hi > lo ? (a - lo) / (hi - lo) : 0); break }
  }
  return {
    zNose: f.zBase + (z.k0 + 1 + phi) * r,
    zFloor: f.zBase + (z.k0 + 1 + Math.min(z.m - 1, Math.floor(phi + 1e-9))) * r,
    level: false,
  }
}

/** the heights over a swept stair: the point's distance up the centreline (projected) */
function sweepHeights(f: StairFacts, p: Pt2): { zNose: number; zFloor: number; level: boolean } {
  const sw = f.layout!.sweep!
  const r = f.calc.riserFt
  let best = Infinity, sAt = 0
  for (let i = 0; i + 1 < sw.pts.length; i += 1) {
    const a = sw.pts[i], b = sw.pts[i + 1]
    const dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy || 1
    const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2))
    const d = Math.hypot(p[0] - a[0] - dx * t, p[1] - a[1] - dy * t)
    if (d < best) { best = d; sAt = sw.s[i] + (sw.s[i + 1] - sw.s[i]) * t }
  }
  // between which two risers: the nosing line climbs one riser from each riser line to the next
  const rs = sw.riserS
  const N = f.calc.treads
  let phi = 0
  if (sAt >= rs[rs.length - 1]) phi = N
  else for (let j = 0; j + 1 < rs.length; j++) if (sAt <= rs[j + 1]) { phi = j + Math.max(0, (sAt - rs[j]) / Math.max(1e-9, rs[j + 1] - rs[j])); break }
  const k = Math.min(N - 1, Math.floor(phi + 1e-9))
  if (sw.landingK.includes(k)) return { zNose: f.zBase + (k + 1) * r, zFloor: f.zBase + (k + 1) * r, level: true }
  return { zNose: f.zBase + Math.min(N + 1, 1 + phi) * r, zFloor: f.zBase + (k + 1) * r, level: false }
}

function heightsAt(f: StairFacts, p: Pt2, leg?: number): { zNose: number; zFloor: number; level: boolean } {
  const L = f.layout!
  if (L.sweep) return sweepHeights(f, p)
  if (leg !== undefined && leg <= -10) return winderHeights(f, -10 - leg, p)
  if (leg !== undefined && leg >= 0 && leg < L.legs.length) return heightsOnLeg(f, leg, p)
  if (leg === undefined) { const zi = winderZoneAt(f, p); if (zi >= 0 && !onAnyLeg(f, p)) return winderHeights(f, zi, p) }
  const r = f.calc.riserFt, td = f.treadFt
  // the flight the point is ON: of the flights whose run covers it, the one it sits most
  // inside (a U's two flights can touch along the well — a guard 1-1/4" in from one flight's
  // edge is 1-1/4" OUTSIDE the other's, and must take its heights from its own flight)
  let pick: { li: number; s: number; out: number } | null = null
  for (let li = 0; li < L.legs.length; li++) {
    const g = L.legs[li]
    const s = (p[0] - g.a[0]) * g.u[0] + (p[1] - g.a[1]) * g.u[1]
    const c = (p[0] - g.a[0]) * g.n[0] + (p[1] - g.a[1]) * g.n[1]
    const sEnd = g.s0 + (g.kEnd - g.kStart) * td
    if (s < g.s0 - 0.02 || s > sEnd + 0.02) continue
    const out = Math.abs(c) - (g.w || f.widthFt) / 2     // < 0 inside, > 0 outside the flight
    if (out > 0.25) continue
    if (!pick || out < pick.out) pick = { li, s, out }
  }
  if (pick && leg !== -1) {
    const g = L.legs[pick.li], s = pick.s
    // a flight that ends at a landing arrives flush with it; the last one climbs to the floor
    const zTopLeg = f.zBase + (pick.li + 1 < L.legs.length ? L.legs[pick.li + 1].z0 : f.riseFt)
    const Z0 = f.zBase + g.z0
    const zNose = Math.min(zTopLeg, Math.max(Z0 + r, Z0 + r + ((s - g.s0) * r) / td))
    const j = Math.floor((s - g.s0) / td + 1e-6)
    const zFloor = Math.min(zTopLeg, s < g.s0 + 1e-6 ? Z0 : Z0 + (j + 1) * r)
    return { zNose, zFloor, level: false }
  }
  // a landing it stands on (or beside), else the floor at whichever end it is past
  let best: { z: number; d: number } | null = null
  for (const l of L.landings) {
    const d = distToPoly(p, l.poly)
    if (!best || d < best.d) best = { z: f.zBase + l.zTop, d }
  }
  if (best && best.d < 0.3) return { zNose: best.z, zFloor: best.z, level: true }
  const g0 = L.legs[0], gN = L.legs[L.legs.length - 1]
  if (g0 && gN) {
    const dStart = Math.hypot(p[0] - (g0.a[0] + g0.u[0] * g0.s0), p[1] - (g0.a[1] + g0.u[1] * g0.s0))
    const eN = gN.s0 + (gN.kEnd - gN.kStart) * td
    const dEnd = Math.hypot(p[0] - (gN.a[0] + gN.u[0] * eN), p[1] - (gN.a[1] + gN.u[1] * eN))
    const z = dStart < dEnd ? f.zBase + g0.z0 : f.zBase + f.riseFt
    if (!best || Math.min(dStart, dEnd) < best.d) return { zNose: z, zFloor: z, level: true }
  }
  const z = best ? best.z : f.zBase + f.riseFt
  return { zNose: z, zFloor: z, level: true }
}

/** is the point over one of the straight flights (its run, within a quarter foot of its sides)? */
function onAnyLeg(f: StairFacts, p: Pt2): boolean {
  for (const g of f.layout!.legs) {
    const s = (p[0] - g.a[0]) * g.u[0] + (p[1] - g.a[1]) * g.u[1]
    const c = (p[0] - g.a[0]) * g.n[0] + (p[1] - g.a[1]) * g.n[1]
    if (s > g.s0 + 1e-6 && s < g.s1 - 1e-6 && Math.abs(c) < (g.w || f.widthFt) / 2 + 0.25) return true
  }
  return false
}

/** distance from a point to a polygon (0 inside) */
function distToPoly(p: Pt2, poly: Pt2[]): number {
  let inside = false, d = Infinity
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j]
    if ((a[1] > p[1]) !== (b[1] > p[1]) && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) inside = !inside
    const dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy || 1
    const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2))
    d = Math.min(d, Math.hypot(p[0] - a[0] - dx * t, p[1] - a[1] - dy * t))
  }
  return inside ? 0 : d
}

/**
 * The guard's line along one side of a stair. `insetFt` sets it in from the stair's edge
 * (negative = outside). null when the stair can't lay out.
 */
export function stairGuardLine(stairEl: Parameters<typeof stairFactsOf>[0], side: 'left' | 'right', insetFt: number): StairGuardPoint[] | null {
  const f = stairFactsOf(stairEl)
  const L = f?.layout
  if (!f || !L) return null
  const c = L.curve
  if (c) {
    // the curve: left of the walker is the inner arc when it turns left
    const inner = (side === 'left') === (c.hand === 1)
    const rad = inner ? c.rIn + insetFt : c.rOut - insetFt
    const r = f.calc.riserFt, N = f.calc.treads
    const out: StairGuardPoint[] = []
    const steps = Math.max(2, Math.ceil(N * 3))
    for (let i = 0; i <= steps; i++) {
      const phi = (N * i) / steps
      const a = c.theta0 + c.hand * phi * c.dTheta
      const zNose = f.zBase + Math.min(f.riseFt, r + phi * r)
      const zFloor = f.zBase + Math.min(f.riseFt, (Math.floor(phi + 1e-6) + 1) * r)
      out.push({ p: [c.center[0] + rad * Math.cos(a), c.center[1] + rad * Math.sin(a)], zNose, zFloor, level: false, leg: -2 })
    }
    return out
  }
  if (L.sweep && L.edgeChains) {
    // a swept stair: its edge, set in, every point with its heights off the centreline
    const ch = L.edgeChains[side === 'left' ? 0 : 1]
    const off = offsetLine(ch, ch.slice(1).map(() => -2), side === 'left' ? -insetFt : insetFt)
    return off.map((q) => ({ p: q.p, ...sweepHeights(f, q.p), leg: -2 }))
  }
  let chain: Pt2[] | null = L.edgeChains ? L.edgeChains[side === 'left' ? 0 : 1] : null
  if (!chain || chain.length < 2) {
    // no edge chains: the flights' own sides
    chain = []
    for (const g of L.legs) {
      const w = (g.w || f.widthFt) / 2 * (side === 'left' ? 1 : -1)
      const e = g.s0 + (g.kEnd - g.kStart) * f.treadFt
      chain.push([g.a[0] + g.u[0] * g.s0 + g.n[0] * w, g.a[1] + g.u[1] * g.s0 + g.n[1] * w], [g.a[0] + g.u[0] * e + g.n[0] * w, g.a[1] + g.u[1] * e + g.n[1] * w])
    }
  }
  // drop repeated points
  chain = chain.filter((q, i) => i === 0 || Math.hypot(q[0] - chain![i - 1][0], q[1] - chain![i - 1][1]) > 1e-6)
  if (chain.length < 2) return null
  // each piece's flight: the flight it climbs along, its points on that flight's edge on this side
  const sideSign = side === 'left' ? 1 : -1
  const legs: number[] = []
  for (let i = 0; i + 1 < chain.length; i++) {
    const a = chain[i], b = chain[i + 1]
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1
    const d: Pt2 = [(b[0] - a[0]) / len, (b[1] - a[1]) / len]
    let found = -1
    for (let li = 0; li < L.legs.length && found < 0; li++) {
      const g = L.legs[li]
      if (d[0] * g.u[0] + d[1] * g.u[1] < 0.99) continue
      const hw = (g.w || f.widthFt) / 2, sEnd = g.s0 + (g.kEnd - g.kStart) * f.treadFt
      const on = (q: Pt2) => {
        const s = (q[0] - g.a[0]) * g.u[0] + (q[1] - g.a[1]) * g.u[1]
        const c = (q[0] - g.a[0]) * g.n[0] + (q[1] - g.a[1]) * g.n[1]
        return Math.abs(c - sideSign * hw) < 0.02 && s > g.s0 - 0.05 && s < sEnd + 0.05
      }
      if (on(a) && on(b)) found = li
    }
    legs.push(found)
  }
  // set in toward the stair: the left chain's interior is to its right, the right chain's to its left
  const off = offsetLine(chain, legs, side === 'left' ? -insetFt : insetFt)
  // each piece's heights come from ONE flight: its own, or — on a landing piece — the flight it
  // runs up into (else the one it came down from), extended level across the landing. A corner
  // where the two pieces disagree (a U's well, where the next flight starts a riser higher) is
  // emitted twice, the rail stepping up there; kinks are added where a nosing line meets a landing.
  const effLeg = (i: number) => {
    if (off[i].leg >= 0) return off[i].leg
    // a piece across a winder turn climbs with the winders
    const mid: Pt2 = [(off[i].p[0] + off[i + 1].p[0]) / 2, (off[i].p[1] + off[i + 1].p[1]) / 2]
    const zi = winderZoneAt(f, mid)
    if (zi >= 0) return -10 - zi
    const next = i + 1 < off.length - 1 ? off[i + 1].leg : -1
    if (next >= 0) return next
    const prev = i > 0 ? off[i - 1].leg : -1
    return prev
  }
  const hFor = (leg: number, p: Pt2) => (leg >= 0 ? heightsOnLeg(f, leg, p) : leg <= -10 ? winderHeights(f, -10 - leg, p) : heightsAt(f, p, -1))
  const out: StairGuardPoint[] = []
  const same = (q: StairGuardPoint, h: { zNose: number; zFloor: number }) => Math.abs(q.zNose - h.zNose) < 1e-6 && Math.abs(q.zFloor - h.zFloor) < 1e-6
  for (let i = 0; i + 1 < off.length; i++) {
    const a = off[i].p, b = off[i + 1].p
    if (Math.hypot(b[0] - a[0], b[1] - a[1]) < 1e-6) continue
    const leg = effLeg(i)
    const ha = hFor(leg, a)
    const prev = out[out.length - 1]
    if (!prev || Math.hypot(prev.p[0] - a[0], prev.p[1] - a[1]) > 1e-6 || !same(prev, ha)) out.push({ p: a, ...ha, leg })
    else prev.leg = leg
    // over winders the line bends continuously: a point every 3"
    const nSub = leg <= -10 ? Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / 0.25) : 0
    const along: number[] = []
    for (let j = 1; j < nSub; j++) along.push(j / nSub)
    for (const t of leg >= 0 ? legKinks(f, leg, a, b) : along) {
      const q: Pt2 = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]
      out.push({ p: q, ...hFor(leg, q), leg })
    }
    out.push({ p: b, ...hFor(leg, b), leg: -1 })
  }
  return out.length >= 2 ? out : null
}
